import React from "react";
import type { FormListFieldData } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Form, Input, Select, Button } from "../../../shared";
import EventPropertyField from "./EventPropertyField";
import { filterByLabel, filterByLabelOrValue } from "../../../../utils/selectFilter";
import { useListSegments } from "../../../../queries/dynamicBonuses/useListSegments";
import { compareFieldOperatorOptions } from "../constants";
import { isConditionApplicableToEvent } from "../conditionApplicability";
import { conditionDisplayLabel } from "../conditionTypeCatalog";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import type {
  CatalogConditionItem,
  CatalogEventField,
  DynamicBonusCatalog,
  FieldOperator,
} from "../types";
import styles from "../stylesDynamicBonuses.module.css";

const shortId = (id: string): string => id.split("-")[0] ?? id;
const PARLAY_LEGS_FIELD = "parlay_legs";
const PARLAY_LEGS_MINIMUM = 2;
// Highest leg count that still fails the minimum — i.e. a 1-leg straight bet.
const PARLAY_LEGS_BELOW_MINIMUM = PARLAY_LEGS_MINIMUM - 1;
const PARLAY_LEGS_MINIMUM_MESSAGE =
  "Parlay Insurance requires at least 2 legs — a 1-leg parlay is a straight bet.";

const fieldHasAllowedValues = (
  fields: CatalogEventField[],
  fieldKey: string | undefined,
): boolean => {
  const vals = fields.find((f) => f.field === fieldKey)?.allowed_values;
  return !!vals && vals.length > 0;
};

// Hidden from authoring (BONENG-959 / BONENG-1031 / BONENG-1030):
// - `user_subscribed` — engine-stamped on series runs (BONENG-842)
// - `applied_offer_matches` — injected on opt-in follow-ups at submit
// - `not_already_awarded` — soft-retired; caps gate duplicates (BONENG-1031)
// - `user_in_segment` — Audience selector injects this at submit (BONENG-1030)
// - `cooldown_since_last_award` / `user_attribute_equals` — no param editors;
//   fail closed if authored. Fallback until catalogs mark them `internal`.
// - Retired type_keys even if an older catalog omits `internal`
const HIDDEN_CONDITION_TYPE_KEYS = new Set([
  "user_subscribed",
  "applied_offer_matches",
  "not_already_awarded",
  "user_not_excluded",
  "feature_flag_enabled",
  "is_first_deposit",
  "user_in_segment",
  "cooldown_since_last_award",
  "user_attribute_equals",
]);

type ConditionTypeOption = {
  label: string;
  value: string;
  description: string;
};

const conditionOptionLabel = (typeKey: string): string =>
  conditionDisplayLabel(typeKey);

const toConditionOption = (item: CatalogConditionItem): ConditionTypeOption => ({
  label: conditionOptionLabel(item.type_key),
  value: item.type_key,
  description: item.description,
});

const isAuthorableCondition = (item: CatalogConditionItem): boolean =>
  !item.internal && !HIDDEN_CONDITION_TYPE_KEYS.has(item.type_key);

const authorableConditionOptions = (
  catalog: DynamicBonusCatalog | undefined,
  eventKey: string | undefined,
  applicableConditionTypes: string[] | undefined,
): ConditionTypeOption[] =>
  (catalog?.conditions ?? [])
    .filter(isAuthorableCondition)
    .filter((item) =>
      isConditionApplicableToEvent(item, eventKey, applicableConditionTypes),
    )
    .map(toConditionOption);

/** Keep a stored (now-hidden) type on an existing row so edit/save does not blank it. */
const conditionOptionsForRow = (
  catalog: DynamicBonusCatalog | undefined,
  storedType: string | undefined,
  eventKey: string | undefined,
  applicableConditionTypes: string[] | undefined,
): ConditionTypeOption[] => {
  const options = authorableConditionOptions(
    catalog,
    eventKey,
    applicableConditionTypes,
  );
  if (!storedType || options.some((option) => option.value === storedType)) {
    return options;
  }
  const catalogItem = (catalog?.conditions ?? []).find(
    (item) => item.type_key === storedType,
  );
  return [
    ...options,
    catalogItem
      ? toConditionOption(catalogItem)
      : {
          label: conditionOptionLabel(storedType),
          value: storedType,
          description: "",
        },
  ];
};

/**
 * Rejects a `parlay_legs` comparison that would let a bet with fewer than
 * {@link PARLAY_LEGS_MINIMUM} legs qualify. The check is operator-aware because
 * the boundary differs between lower-bound (`gt`/`gte`/`eq`) and upper-bound
 * (`lt`/`lte`) comparisons. Returns `false` until an operator is chosen so a
 * half-filled row does not flag an error prematurely.
 */
const shouldRejectParlayLegsMinimum = (
  comparedField: unknown,
  operator: unknown,
  value: unknown,
): boolean => {
  if (comparedField !== PARLAY_LEGS_FIELD || operator == null || operator === "") {
    return false;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return false;
  }

  switch (String(operator)) {
    case "eq":
    case "gte":
      // A threshold below 2 admits (or equals) a 1-leg bet.
      return numericValue < PARLAY_LEGS_MINIMUM;
    case "gt":
      // "> 1" already means >= 2 legs; only "> 0" (or lower) admits 1 leg.
      return numericValue < PARLAY_LEGS_BELOW_MINIMUM;
    case "lt":
      // "< 2" (or any bound above 1) admits a 1-leg bet.
      return numericValue > PARLAY_LEGS_BELOW_MINIMUM;
    case "lte":
      // "<= 1" (or any bound reaching 1) admits a 1-leg bet.
      return numericValue >= PARLAY_LEGS_BELOW_MINIMUM;
    default:
      return false;
  }
};

/** Comparison operators offered when a selected field has no catalog condition
 * hint (e.g. `parlay_legs` before pr0-backend catalogs it, or no event chosen
 * yet) — mirrors the pre-catalog hardcoded operator list. */
const FALLBACK_COMPARE_OPERATORS: FieldOperator[] = ["eq", "ne", "gt", "gte", "lt", "lte"];

/**
 * Operator options for an `event_field_compare` row, driven by the selected
 * field's catalog condition hint (`CatalogEventField.conditions[].operators`).
 * Falls back to the legacy hardcoded comparison operators when the field
 * isn't in the catalog yet.
 */
const compareOperatorOptions = (
  fields: CatalogEventField[],
  fieldValue: string | undefined,
) => {
  const hint = fields
    .find((f) => f.field === fieldValue)
    ?.conditions.find((c) => c.type_key === "event_field_compare");
  const allowed = hint?.operators ?? FALLBACK_COMPARE_OPERATORS;
  return compareFieldOperatorOptions().filter((o) =>
    (allowed as string[]).includes(o.value),
  );
};

type ConditionParamFieldsProps = {
  name: number;
  namePath: (string | number)[];
  restField: Omit<FormListFieldData, "name" | "key">;
  fields: CatalogEventField[];
  segmentOptions: Array<{ label: string; value: string }>;
  segmentsLoading: boolean;
};

/** Param inputs for a single condition row — watches type so fields appear on selection. */
const ConditionParamFields: React.FC<ConditionParamFieldsProps> = ({
  name,
  namePath,
  restField,
  fields,
  segmentOptions,
  segmentsLoading,
}) => {
  const condType =
    (Form.useWatch([...namePath, name, "type"]) as string | undefined) ?? "";
  const comparedFieldValue = Form.useWatch([
    ...namePath,
    name,
    "params",
    "field",
  ]) as string | undefined;
  const form = Form.useFormInstance();

  if (condType === "min_event_amount" || condType === "min_cumulative_deposits") {
    return (
      <Form.Item
        {...restField}
        name={[name, "params", "amount"]}
        rules={[{ required: true, message: "Amount required" }]}
        style={{ flex: "0 0 130px", marginBottom: 0 }}
      >
        <Input placeholder="Amount (e.g. 10.00)" />
      </Form.Item>
    );
  }
  if (condType === "user_in_segment") {
    return (
      <Form.Item
        {...restField}
        name={[name, "params", "segment_id"]}
        rules={[{ required: true, message: "Select a segment" }]}
        style={{ flex: "1 1 200px", marginBottom: 0 }}
      >
        <Select
          placeholder={segmentsLoading ? "Loading segments…" : "Select a segment"}
          options={segmentOptions}
          loading={segmentsLoading}
          showSearch
          filterOption={filterByLabel}
        />
      </Form.Item>
    );
  }
  if (condType === "event_field_equals") {
    const allowedValues = fields.find((f) => f.field === comparedFieldValue)
      ?.allowed_values;
    const valueOptions =
      allowedValues && allowedValues.length > 0
        ? allowedValues.map((v) => ({ label: v, value: v }))
        : null;

    return (
      <>
        <Form.Item
          {...restField}
          name={[name, "params", "field"]}
          rules={[{ required: true, message: "Field required" }]}
          style={{ flex: "0 0 160px", marginBottom: 0 }}
        >
          <EventPropertyField
            fields={fields}
            placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_EVENT_FIELD}
            onChange={(newField) => {
              if (
                fieldHasAllowedValues(fields, comparedFieldValue) !==
                fieldHasAllowedValues(fields, newField)
              ) {
                form.setFieldValue(
                  [...namePath, name, "params", "value"],
                  undefined,
                );
              }
            }}
          />
        </Form.Item>
        <Form.Item
          {...restField}
          name={[name, "params", "value"]}
          rules={[{ required: true, message: "Value required" }]}
          style={{ flex: "0 0 130px", marginBottom: 0 }}
        >
          {valueOptions ? (
            <Select placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_VALUE} options={valueOptions} />
          ) : (
            <Input placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_VALUE} />
          )}
        </Form.Item>
      </>
    );
  }
  if (condType === "event_field_compare") {
    return (
      <>
        <Form.Item
          {...restField}
          name={[name, "params", "field"]}
          rules={[{ required: true, message: "Field required" }]}
          style={{ flex: "0 0 160px", marginBottom: 0 }}
        >
          <EventPropertyField
            fields={fields}
            placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_EVENT_FIELD}
          />
        </Form.Item>
        <Form.Item
          {...restField}
          name={[name, "params", "op"]}
          rules={[{ required: true, message: "Operator required" }]}
          style={{ flex: "0 0 150px", marginBottom: 0 }}
        >
          <Select
            placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_OPERATOR}
            options={compareOperatorOptions(fields, comparedFieldValue)}
          />
        </Form.Item>
        <Form.Item
          {...restField}
          name={[name, "params", "value"]}
          rules={[
            { required: true, message: "Value required" },
            ({ getFieldValue: getCurrentFieldValue }) => ({
              validator(_, value) {
                const paramsPath = [...namePath, name, "params"];
                const comparedField = getCurrentFieldValue([...paramsPath, "field"]);
                const operator = getCurrentFieldValue([...paramsPath, "op"]);

                if (shouldRejectParlayLegsMinimum(comparedField, operator, value)) {
                  return Promise.reject(new Error(PARLAY_LEGS_MINIMUM_MESSAGE));
                }
                return Promise.resolve();
              },
            }),
          ]}
          style={{ flex: "0 0 130px", marginBottom: 0 }}
        >
          <Input placeholder={AppStrings.PAM_DYNAMIC_BONUS_PLACEHOLDER_VALUE} />
        </Form.Item>
      </>
    );
  }
  if (condType === "max_awards_per_user") {
    return (
      <Form.Item
        {...restField}
        name={[name, "params", "limit"]}
        rules={[{ required: true, message: "Limit required" }]}
        style={{ flex: "0 0 130px", marginBottom: 0 }}
      >
        <Input placeholder="Limit (e.g. 1)" />
      </Form.Item>
    );
  }

  // Conditions with no params (applied_offer_matches, referral_code_is_affiliate,
  // campaign_affiliate_code_matches, user_was_referred, not_already_awarded,
  // etc.) need no extra inputs — catalog can ship new param-less types without
  // a FE change. Retired authoring types (feature_flag_enabled,
  // is_first_deposit, user_not_excluded) are filtered from the picker.
  return null;
};

type Props = {
  /** Absolute path from form root — drives useWatch for reactive param fields. */
  namePath: (string | number)[];
  /**
   * Form.List name. Defaults to `namePath`. When nested inside the steps
   * Form.List, pass a relative path such as `[stepIndex, "conditions"]`.
   */
  listName?: (string | number)[];
  catalog: DynamicBonusCatalog | undefined;
  /** filterable_fields for the selected trigger event — drives the field and
   * operator pickers for event_field_equals / event_field_compare. Pass an
   * empty array when no trigger event is selected (e.g. referral eligibility). */
  fields?: CatalogEventField[];
  /** Selected trigger event_key — used with catalog applicability metadata. */
  eventKey?: string;
  /**
   * Per-event allowlist from GET .../events (`applicable_condition_types`).
   * When present, this is the strongest "event declares support" signal.
   * Omit on older backends so catalog keys / fail-open apply instead.
   */
  applicableConditionTypes?: string[];
  /** When false, hide the Add condition control (e.g. show_bonus offer steps). */
  allowAdd?: boolean;
  /**
   * When true, suppress the "No conditions for this event" empty-state
   * notice even with zero condition rows — used when the step already shows
   * other award-specific content (e.g. the Split Count / Amount result
   * param inputs) above this list, so the box isn't actually empty.
   */
  hideEmptyState?: boolean;
};

/**
 * Inline condition row list inside a step card.
 * Uses Form.List so each condition gets its own validated sub-fields.
 */
const ConditionsField: React.FC<Props> = ({
  namePath,
  listName = namePath,
  catalog,
  fields = [],
  eventKey,
  applicableConditionTypes,
  allowAdd = true,
  hideEmptyState = false,
}) => {
  const storedConditions = Form.useWatch(namePath) as
    | Array<{ type?: string }>
    | undefined;

  const { data: segments, isLoading: segmentsLoading } = useListSegments();
  const segmentOptions = (segments ?? []).map((s) => ({
    label: `${s.name} (${shortId(s.segment_id)})`,
    value: s.segment_id,
  }));

  return (
    <Form.List name={listName}>
      {(conditionRows, { add, remove }) => (
        <div className={styles.conditionsList}>
          {conditionRows.length === 0 && !hideEmptyState && (
            <div
              className={`${styles.infoBox} ${styles.conditionsEmptyState}`}
              role="status"
              data-testid="conditions-empty-state"
            >
              {AppStrings.PAM_DYNAMIC_BONUS_NO_CONDITIONS_FOR_EVENT}
            </div>
          )}
          {conditionRows.map((field) => {
            const { key, name, ...restField } = field;
            return (
              <div key={key} className={styles.conditionRow}>
                <div className={styles.conditionRowFields}>
                  <Form.Item
                    {...restField}
                    name={[name, "type"]}
                    rules={[{ required: true, message: "Pick a condition type" }]}
                    style={{ flex: "1 1 200px", marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Condition type"
                      options={conditionOptionsForRow(
                        catalog,
                        storedConditions?.[name]?.type,
                        eventKey,
                        applicableConditionTypes,
                      )}
                      showSearch
                      filterOption={filterByLabelOrValue}
                    />
                  </Form.Item>

                  <ConditionParamFields
                    name={name}
                    namePath={namePath}
                    restField={restField}
                    fields={fields}
                    segmentOptions={segmentOptions}
                    segmentsLoading={segmentsLoading}
                  />
                </div>

                <Button
                  intent="danger"
                  variant="ghost"
                  iconLeft={<DeleteOutlined />}
                  onClick={() => remove(name)}
                  aria-label="Remove condition"
                  style={{ flexShrink: 0 }}
                />
              </div>
            );
          })}

          {allowAdd && (
            <Button
              intent="neutral"
              variant="ghost"
              iconLeft={<PlusOutlined />}
              onClick={() => add({ type: "", params: {} })}
              className={styles.rulesAddAction}
              data-testid="conditions-add-condition"
            >
              {AppStrings.PAM_DYNAMIC_BONUS_ADD_CONDITION}
            </Button>
          )}
        </div>
      )}
    </Form.List>
  );
};

export default ConditionsField;
